import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CalendarClock, Clock, Pencil, PenLine, Play, Plus, Rss, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, timeAgo } from "@/lib/format";
import { ConfirmDelete, PageHeader, cadenceText } from "@/admin/ui";
import { AutomationDialog } from "@/admin/AutomationDialog";
import { RunDialog } from "@/admin/RunProgress";
import { useAdminCategories, useAutomationActions, useAutomations, useRefresh } from "@/admin/hooks";

const HowItWorks = () => (
  <div className="grid md:grid-cols-3 bg-ink text-slate-300 mb-8" data-testid="automations-how-it-works">
    {[["01", "Elige qué escribir", "Replicar titulares de tus fuentes o artículos propios sobre tus temas."], ["02", "Elige cuándo", "Cada pocas horas o a horas fijas los días que quieras."], ["03", "Se publica sola", "Con imagen, claves, FAQs y SEO. O como borrador para revisar."]].map(([n, t, d]) => (
      <div key={n} className="p-6 border-b md:border-b-0 md:border-r border-white/10 last:border-0">
        <p className="font-display font-black text-3xl text-gold">{n}</p>
        <p className="font-semibold text-white mt-2">{t}</p>
        <p className="text-sm mt-1">{d}</p>
      </div>
    ))}
  </div>
);

const Card = ({ a, cats, actions, onEdit, onDelete }) => {
  const cat = cats?.find((c) => c.slug === a.category)?.name || "Automática";
  return (
    <article className={`bg-card border p-6 flex flex-col transition-colors ${a.enabled ? "border-border" : "border-dashed border-border opacity-80"}`} data-testid={`automation-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="kicker text-[10px] inline-flex items-center gap-1.5 text-primary">{a.type === "rss" ? <Rss className="h-3.5 w-3.5" /> : <PenLine className="h-3.5 w-3.5" />}{a.type === "rss" ? "Replicar noticias" : "Contenido propio"}</span>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">{a.enabled ? "Activa" : "En pausa"}<Switch checked={a.enabled} onCheckedChange={() => actions.toggle.mutate(a)} data-testid={`automation-toggle-${a.id}`} /></label>
      </div>
      <h2 className="font-display font-bold text-2xl mt-3" data-testid={`automation-name-${a.id}`}>{a.name}</h2>
      <p className="flex items-center gap-2 text-sm mt-2"><Clock className="h-4 w-4 text-muted-foreground" />{cadenceText(a)}</p>
      <p className="text-sm text-muted-foreground mt-1">{a.count} noticia{a.count > 1 ? "s" : ""} cada vez · Sección: {cat} · {a.publish ? "Publica directamente" : "Deja borrador"}{a.generate_image ? " · Imagen IA" : ""}</p>
      {a.type === "topic" && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">Temas: {a.topics.join(" · ")}</p>}
      <div className="mt-5 pt-4 border-t border-border grid grid-cols-2 gap-3 text-xs">
        <div><p className="kicker text-[9px] text-muted-foreground">Próxima</p><p className="mt-1 flex items-center gap-1.5" data-testid={`automation-next-${a.id}`}><CalendarClock className="h-3.5 w-3.5 text-primary" />{a.enabled && a.next_run_at ? `${timeAgo(a.next_run_at)} · ${fmtDateTime(a.next_run_at)}` : "—"}</p></div>
        <div><p className="kicker text-[9px] text-muted-foreground">Última</p><p className="mt-1">{a.last_run_at ? timeAgo(a.last_run_at) : "Nunca"}</p></div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => actions.run.mutate(a)} disabled={actions.run.isPending} data-testid={`automation-run-${a.id}`}><Play /> Ejecutar ahora</Button>
        <Button size="sm" variant="outline" onClick={() => onEdit(a)} data-testid={`automation-edit-${a.id}`}><Pencil /> Editar</Button>
        <ConfirmDelete title="¿Eliminar esta automatización?" description="Las noticias ya publicadas no se borran." onConfirm={() => onDelete(a)}>
          <Button size="sm" variant="ghost" className="hover:text-destructive ml-auto" data-testid={`automation-delete-${a.id}`}><Trash2 /></Button>
        </ConfirmDelete>
      </div>
    </article>
  );
};

export default function Automations() {
  const { data, isLoading } = useAutomations();
  const { data: cats } = useAdminCategories();
  const actions = useAutomationActions();
  const refresh = useRefresh();
  const [dialog, setDialog] = useState({ open: false, automation: null });
  const remove = useMutation({
    mutationFn: (a) => api.delete(`/admin/automations/${a.id}`),
    onSuccess: () => { toast.success("Automatización eliminada"); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div data-testid="admin-automations-page">
      <PageHeader kicker="Piloto automático" title="Automatizaciones" description="Configura una vez y tu periódico se actualiza solo con la cadencia que quieras.">
        <Button onClick={() => setDialog({ open: true, automation: null })} data-testid="automations-new-btn"><Plus /> Nueva automatización</Button>
      </PageHeader>
      <HowItWorks />
      {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      <div className="grid md:grid-cols-2 gap-4">
        {data?.map((a) => <Card key={a.id} a={a} cats={cats} actions={actions} onEdit={(x) => setDialog({ open: true, automation: x })} onDelete={remove.mutate} />)}
      </div>
      {data && !data.length && <p className="text-muted-foreground py-10 text-center" data-testid="automations-empty">Aún no tienes automatizaciones. Crea la primera.</p>}
      <AutomationDialog open={dialog.open} automation={dialog.automation} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />
      <RunDialog runId={actions.runId} onClose={() => actions.setRunId(null)} />
    </div>
  );
}
